import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Popover } from 'components/atoms/Popover';
import { DropdownCaret } from 'components/atoms/DropdownCaret';
import {
  FilterListbox,
  FilterListboxOption,
} from 'components/molecules/FilterListbox';
import { ScheduleFilterOption } from './useScheduleFilter';

interface ScheduleFiltersProps {
  /** When false the dropdown is not rendered (brand has no categories/vibes, or both disabled). */
  readonly enabled: boolean;
  readonly options: ReadonlyArray<ScheduleFilterOption>;
  readonly selectedValues: ReadonlyArray<string>;
  readonly onChange: (values: ReadonlyArray<string>) => void;
}

interface FilterOptionItemProps {
  readonly label: string;
  readonly value: string;
  readonly selected: boolean;
  readonly onToggle: (value: string) => void;
}

const FilterOptionItem = ({
  label,
  value,
  selected,
  onToggle,
}: FilterOptionItemProps) => {
  const handleSelect = () => onToggle(value);
  return (
    <FilterListboxOption
      ariaLabel={label}
      className="schedule-view__filter-option"
      onSelect={handleSelect}
      selected={selected}
    >
      <span className="schedule-view__filter-option-label">{label}</span>
    </FilterListboxOption>
  );
};

/**
 * Guest filter for the schedule view: a borderless popover dropdown that lets the user toggle
 * multiple categories and/or vibes at once. Uses the Popover atom (Radix popover, not Select) so
 * the menu anchors correctly when the nav row is sticky. Each option toggles its own membership
 * in the selection; the menu stays open while the user multi-selects. The "All experiences" row
 * clears the selection entirely.
 */
const ScheduleFilters = ({
  enabled,
  options,
  selectedValues,
  onChange,
}: ScheduleFiltersProps) => {
  const { t: translate } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!enabled) return null;

  const allLabel = translate('allExperiences', {
    defaultValue: 'All experiences',
  });

  const selectedSet = new Set(selectedValues);
  const triggerLabel = (() => {
    if (selectedSet.size === 0) return allLabel;
    if (selectedSet.size === 1) {
      const only = selectedValues[0];
      return options.find((option) => option.value === only)?.label ?? allLabel;
    }
    return translate('countSelected', {
      defaultValue: '{{count}} selected',
      count: selectedSet.size,
    });
  })();

  const handleClearAll = () => {
    onChange([]);
  };

  const handleToggle = (value: string) => {
    if (selectedSet.has(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const filterAriaLabel = translate('filterExperiences', {
    defaultValue: 'Filter experiences',
  });

  return (
    <FilterSelectWrapper className="schedule-view__filter-select">
      <Popover
        asChild
        portalled
        open={open}
        onOpenChange={setOpen}
        align="end"
        side="bottom"
        sideOffset={6}
        aria-label={filterAriaLabel}
        trigger={
          <FilterTrigger
            aria-expanded={open}
            aria-haspopup="listbox"
            className="schedule-view__filter-button"
            type="button"
          >
            <span className="schedule-view__filter-label">{triggerLabel}</span>
            <DropdownCaret
              className="schedule-view__filter-caret"
              open={open}
            />
          </FilterTrigger>
        }
      >
        <FilterListbox ariaLabel={filterAriaLabel} multiSelectable>
          <FilterListboxOption
            ariaLabel={allLabel}
            className="schedule-view__filter-option schedule-view__filter-option--all"
            onSelect={handleClearAll}
            selected={selectedSet.size === 0}
          >
            <span className="schedule-view__filter-option-label">
              {allLabel}
            </span>
          </FilterListboxOption>
          {options.map((option) => (
            <FilterOptionItem
              key={option.value}
              label={option.label}
              value={option.value}
              selected={selectedSet.has(option.value)}
              onToggle={handleToggle}
            />
          ))}
        </FilterListbox>
      </Popover>
    </FilterSelectWrapper>
  );
};

export default ScheduleFilters;

const FilterSelectWrapper = styled.div`
  /* Content-width by default so NavRow's space-between pins the filter to the right edge, but
     shrinkable (flex-shrink: 1) so the wrapper never pushes the day strip below its content
     floor when the selected label is long. */
  flex: 0 1 auto;
  min-width: 0;
`;

const FilterTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 1 auto;
  max-width: 100%;
  min-width: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.2;
  color: var(--way-colors-primaryTextColor);
  white-space: nowrap;
  padding: 0;

  /* Cap the visible label and ellipsize the overflow so a long category name can't push the
     trigger wide enough to collide with the day strip. 20ch fits typical category names in full
     and truncates anything longer. */
  .schedule-view__filter-label {
    display: inline-block;
    max-width: 20ch;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;
    white-space: nowrap;
  }
`;
